import { z } from "zod";
import { phoneNumbersMatch } from "@core/schemas/fields/phone";

type PhoneSchema = {
  phoneNumber?: string;
  confirmPhoneNumber?: string;
};

/**
 * Adds phone confirmation validation to a schema.
 * Requires the schema to have `phoneNumber` and `confirmPhoneNumber` fields.
 * Only validates if phoneNumber is provided (both fields are optional).
 * Uses E.164 normalization for comparison.
 *
 * @example
 * const schema = withPhoneConfirmation(z.object({
 *   phoneNumber: phoneNumberField,
 *   confirmPhoneNumber: confirmPhoneNumberField,
 *   // ... other fields
 * }));
 */
export const withPhoneConfirmation = <
  T extends z.ZodTypeAny,
  Output extends PhoneSchema = z.infer<T>
>(
  schema: T
): z.ZodEffects<T, Output, z.input<T>> =>
  schema.refine(
    (data: PhoneSchema) => {
      // Only validate if phone number is provided
      if (!data.phoneNumber) return true;
      return phoneNumbersMatch(data.phoneNumber, data.confirmPhoneNumber);
    },
    {
      message: "Phone numbers must match",
      path: ["confirmPhoneNumber"],
    }
  ) as z.ZodEffects<T, Output, z.input<T>>;
